import React, { useState, useEffect } from 'react';
import { string, number, shape, bool, arrayOf } from 'prop-types';

import classnames from 'classnames';
import ProgressIndicatorLinear from '@andes/progress-indicator-linear';

import Title from '../title';
import IconChevron from '../../../../components-v2/icons/sources/chevron';
import { trackEvent } from '../../../../lib/melidata';
import { Subtitle } from './subtitle';

/**
 * @param {Object} props
 * @param {boolean} props.devices
 * @param {{ text: string, values: { text: string }[] }} props.epigraph
 * @param {string} props.group_by
 * @param {string} props.picture
 * @param {Array} props.pills
 * @param {{percent: number, accessibility_text: string}} props.progress_bar
 * @param {Object} props.show_more
 * @param {Object} props.title
 * @param {boolean} props.shouldRenderIcons
 * @param {string} props.nameSpace
 * @param {Object} props.currentInterventionData
 * @param {Object} props.promotedLink
 */
const CardHeader = ({
  devices,
  epigraph,
  group_by,
  picture,
  pills = [],
  progress_bar,
  show_more,
  title,
  shouldRenderIcons = true,
  nameSpace,
  currentInterventionData,
  promotedLink,
}) => {
  const [fsBarData, setFsBarData] = useState({ title, epigraph, progress_bar });
  const namespace = `${nameSpace}__header`;

  useEffect(() => {
    if (currentInterventionData?.header && currentInterventionData?.id === group_by) {
      setFsBarData(currentInterventionData?.header);
    }
  }, [currentInterventionData, fsBarData, group_by]);

  const handleClick = () => {
    const { track } = show_more;

    if (track) {
      trackEvent({
        clean: true,
        path: track?.path,
        event_data: { ...track?.event_data },
      });
    }
  };

  return (
    <div className={namespace}>
      {fsBarData.title?.text && (
        <Title
          {...fsBarData.title}
          devices={devices}
          picture={picture}
          pills={pills}
          group_by={group_by}
          shouldRenderIcons={shouldRenderIcons}
          nameSpace={nameSpace}
          promotedLink={promotedLink}
          currentInterventionData={currentInterventionData}
        />
      )}
      {fsBarData?.progress_bar && fsBarData?.progress_bar?.percent > 0 && (
        <div className="content-progrees">
          <ProgressIndicatorLinear
            highlight
            size="small"
            srLabel=""
            color={fsBarData?.progress_bar?.color}
            value={fsBarData?.progress_bar?.percent}
          />
        </div>
      )}
      <Subtitle
        className={classnames('subtitle', { 'subtitle--mt': !Boolean(progress_bar) })}
        epigraph={fsBarData?.epigraph}
      />
      {!devices && !!show_more && (
        <a
          className="subtitle__link"
          href={show_more.target}
          onClick={handleClick}
          data-testid="see_all_products"
          aria-hidden="true"
          type="button"
          aria-label={show_more.label.accessibility_text || ''}
          aria-live="polite"
        >
          {show_more.label.text}
          <IconChevron />
        </a>
      )}
    </div>
  );
};

CardHeader.propTypes = {
  currentInterventionData: shape({
    id: string,
    header: shape({
      epigraph: shape({
        text: string,
        values: arrayOf(
          shape({
            text: string,
          }),
        ),
      }),
      progress_bar: shape({
        accessibility_text: string,
        percent: number,
      }),
      title: shape({
        text: string,
      }),
    }),
  }),
  devices: bool,
  epigraph: shape({
    text: string,
    values: arrayOf(
      shape({
        text: string,
      }),
    ),
  }),
  group_by: string,
  nameSpace: string,
  picture: string,
  pills: arrayOf(
    shape({
      accessibility_text: string,
      color: bool,
      text: string,
      values: arrayOf(
        shape({
          id: string,
        }),
      ),
    }),
  ),
  progress_bar: shape({
    accessibility_text: string,
    percent: number,
  }),
  promotedLink: shape({
    icon: shape({
      icon_color: string,
      icon_name: string,
    }),
    target: string,
    text: string,
  }),
  shouldRenderIcons: bool,
  show_more: shape({
    label: shape({
      text: string,
    }),
    target: string,
    track: shape({
      event_data: shape({
        flow_type: string,
        got_free_shipping: bool,
        group_type: string,
        intervention_position: number,
        intervention_tracking_id: string,
        view_all_products_link: string,
      }),
      path: string,
    }),
  }),
  title: shape({
    mobile: bool,
    text: string,
    values: arrayOf(
      shape({
        text: string,
      }),
    ),
  }),
};

export default CardHeader;
